import type { FlatRouteConfig, RouteConfig } from './types';

/**
 * Flattens a nested {@link RouteConfig} tree into a flat list, concatenating
 * paths and accumulating `layout`s from ancestors (adapted from
 * gpm-webapp-v2's AppRouter).
 *
 * Emission rules per node:
 * - has `element` → renderable route at the concatenated path;
 * - `layout` only, no children → route rendering the layout with empty content;
 * - neither `element`/`layout` nor children → emitted bare (AppRouter renders
 *   the 404 page there);
 * - children are always recursed into with the accumulated path/layouts.
 */
export const flattenRoutes = (
  routes: RouteConfig[],
  parentPath = '',
  parentLayouts: NonNullable<FlatRouteConfig['layouts']> = []
): FlatRouteConfig[] => {
  const flatRoutes: FlatRouteConfig[] = [];

  for (const route of routes) {
    const fullPath = parentPath + route.path;
    const currentLayouts = route.layout ? [...parentLayouts, route.layout] : parentLayouts;
    const hasChildren = Boolean(route.children && route.children.length > 0);

    if (route.element) {
      flatRoutes.push({
        path: fullPath,
        element: route.element,
        layouts: currentLayouts.length > 0 ? currentLayouts : undefined,
        noRootLayout: route.noRootLayout,
      });
    } else if (route.layout && !hasChildren) {
      flatRoutes.push({
        path: fullPath,
        element: undefined,
        layouts: currentLayouts.length > 0 ? currentLayouts : undefined,
        noRootLayout: route.noRootLayout,
      });
    } else if (!route.layout && !hasChildren) {
      flatRoutes.push({
        path: fullPath,
        element: undefined,
        layouts: undefined,
        noRootLayout: route.noRootLayout,
      });
    }

    if (hasChildren) {
      flatRoutes.push(...flattenRoutes(route.children!, fullPath, currentLayouts));
    }
  }

  return flatRoutes;
};
